import { useLayoutEffect, useRef } from "react";

/**
 * Auto-scales text to always fit inside its parent width on a single line.
 * Binary-searches font-size between `min` and `max` pixels, observes resize.
 */
export default function FitText({
  children,
  min = 16,
  max = 96,
  className = "",
}) {
  const wrapRef = useRef(null);
  const textRef = useRef(null);

  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    const text = textRef.current;
    if (!wrap || !text) return;

    const fit = () => {
      if (!wrap.clientWidth) return;
      let lo = min;
      let hi = max;
      // guard: shrink first to measure baseline
      text.style.fontSize = min + "px";
      // binary search largest size that fits
      while (lo < hi) {
        const mid = Math.floor((lo + hi + 1) / 2);
        text.style.fontSize = mid + "px";
        if (text.scrollWidth <= wrap.clientWidth) {
          lo = mid;
        } else {
          hi = mid - 1;
        }
      }
      text.style.fontSize = lo + "px";
    };

    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(wrap);
    // Also re-fit on font load (custom webfonts arriving async)
    if (document?.fonts?.ready) {
      document.fonts.ready.then(fit).catch(() => {});
    }
    return () => ro.disconnect();
  }, [children, min, max]);

  return (
    <div ref={wrapRef} className={`w-full overflow-hidden ${className}`}>
      <span
        ref={textRef}
        className="inline-block whitespace-nowrap leading-none"
        style={{ fontSize: min + "px" }}
      >
        {children}
      </span>
    </div>
  );
}
